import { useAuth } from "@clerk/nextjs";
import {
  CREDIT_RESET_INTERVAL_DAYS,
  CREDIT_FORCE_RESET_DATES,
} from "constants/config";
import useClerkMetadata from "features/User/hooks/useClerkMetadata";
import { UsageData } from "helpers/api";
import { ensureUsageData } from "helpers/subscription";
import { useCallback, useEffect } from "react";
import useSessionUsage from "./useSessionUsage";

// After CREDIT_RESET_INTERVAL_DAYS, we reset a user's usage back to zero. This logic lives here on the client side, and we check at the start of each session to see if we should reset usage.
// We use a global singleton to track if we've already reset usage this session in order to prevent multiple reset operations from happening simultaneously if this hook is used within multiple components.
let hasResetUsageThisSession = false;

// Testing utility to reset the internal state
export const __resetUsageSessionState = () => {
  hasResetUsageThisSession = false;
};

/**
 * Determines if the user's usage data should be reset
 * Checks both regular interval and force reset dates
 */
function shouldResetUsage(lastResetDateString: unknown): boolean {
  const msInDay = 24 * 60 * 60 * 1000;

  // If value is unset or invalid, reset the usage.
  if (typeof lastResetDateString !== "string") {
    return true;
  }

  const lastResetDate = new Date(lastResetDateString);
  const currentDate = new Date();

  // Check if the date is invalid
  if (isNaN(lastResetDate.getTime())) {
    return true;
  }

  // Check for force reset dates
  for (const resetDateStr of CREDIT_FORCE_RESET_DATES) {
    const forceResetDate = new Date(resetDateStr);

    // Check if this force reset date is:
    // 1. After the last reset
    // 2. On or before current date
    // 3. Valid date
    if (
      !isNaN(forceResetDate.getTime()) &&
      lastResetDate < forceResetDate &&
      currentDate >= forceResetDate
    ) {
      return true;
    }
  }

  // Check regular interval reset
  const daysDiff = Math.round(
    Math.abs((Number(currentDate) - Number(lastResetDate)) / msInDay)
  );

  return daysDiff >= CREDIT_RESET_INTERVAL_DAYS;
}

/**
 * An outrageously-named hook for managing and tracking user feature usage.
 *
 * The hook maintains this usage data in the user's metadata and provides
 * methods to increment usage of a feature. It also includes an automatic
 * reset mechanism that triggers when the configured reset interval has passed.
 */
export const useUserUsage = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const isNotSignedIn = !isSignedIn && isLoaded;

  const { userMetadata, updateUserMetadata, isUserMetadataLoaded } =
    useClerkMetadata();

  const { usage: sessionUsage, updateUsage: updateSessionUsage } =
    useSessionUsage();

  // If the user is not signed in, we use the session usage. Otherwise, we use the clerk usage.
  const usage = ensureUsageData(isNotSignedIn ? sessionUsage : userMetadata);

  const updateUsage = useCallback(
    (usageData: Partial<UsageData>) => {
      if (isNotSignedIn) {
        updateSessionUsage(usageData);
      } else {
        updateUserMetadata(usageData);
      }
    },
    [isNotSignedIn, updateSessionUsage, updateUserMetadata]
  );

  const isUsageSet = isNotSignedIn
    ? sessionUsage !== undefined
    : isUserMetadataLoaded;

  const appendConsensusSnapshotUsedId = (searchId: string) => {
    if (!usage) return;
    if (!usage.used_consensus_snapshot_queries.includes(searchId)) {
      updateUsage({
        used_consensus_snapshot_queries: [
          ...usage.used_consensus_snapshot_queries,
          searchId,
        ],
      });
    }
  };

  const appendStudyDetailsUsedPaperId = (paperId: string) => {
    if (!usage) return;
    if (!usage.used_study_paper_ids.includes(paperId)) {
      updateUsage({
        used_study_paper_ids: [...usage.used_study_paper_ids, paperId],
      });
    }
  };

  const updateAskPaperMessageCountForPaper = (counts: {
    [key: string]: number;
  }) => {
    if (!usage) return;
    updateUsage({
      ask_paper_messages_per_paper_count: {
        ...usage.ask_paper_messages_per_paper_count,
        ...counts,
      },
    });
  };

  const appendQuickSearchUsedId = (searchId: string) => {
    if (!usage) return;
    if (!usage.used_quick_search_queries.includes(searchId)) {
      updateUsage({
        used_quick_search_queries: [
          ...usage.used_quick_search_queries,
          searchId,
        ],
      });
    }
  };

  const appendProSearchUsedId = (searchId: string) => {
    if (!usage) return;
    if (!usage.used_pro_search_queries.includes(searchId)) {
      updateUsage({
        used_pro_search_queries: [...usage.used_pro_search_queries, searchId],
      });
    }
  };

  const appendDeepSearchUsedId = (searchId: string) => {
    if (!usage) return;
    if (!usage.used_deep_search_queries.includes(searchId)) {
      updateUsage({
        used_deep_search_queries: [...usage.used_deep_search_queries, searchId],
      });
    }
  };

  const resetUsage = useCallback(async () => {
    await updateUsage({
      ask_paper_messages_per_paper_count: {},
      last_reset_date: new Date().toISOString(),
      used_study_paper_ids: [],
      used_consensus_snapshot_queries: [],
      used_quick_search_queries: [],
      used_pro_search_queries: [],
      used_deep_search_queries: [],
      bonus_deep_search_credits: 0,
    });
  }, [updateUsage]);

  useEffect(() => {
    // Usage isn't loaded yet
    if (!isUsageSet) return;

    // We've already reset this session
    if (hasResetUsageThisSession) return;

    // Check if we should reset usage
    if (!shouldResetUsage(usage.last_reset_date)) return;

    hasResetUsageThisSession = true;
    resetUsage();
  }, [isUsageSet, usage, resetUsage]);

  return {
    usage,
    isUsageSet,
    isNotSignedIn,
    appendConsensusSnapshotUsedId,
    appendStudyDetailsUsedPaperId,
    updateAskPaperMessageCountForPaper,
    appendQuickSearchUsedId,
    appendProSearchUsedId,
    appendDeepSearchUsedId,
    resetUsage,
  };
};
